import { z } from "zod";
import { consensusBallotViewSchema, seatVotedByOtherSchema } from "./vote.js";

/* ===========================================================================
 * US-E17.F6-0193 — BIỂU QUYẾT PHI-EXPENSE (governance) · @UC9268 @UC9269
 *
 * 🔴 GOVERNANCE = quyết định KHÔNG-TIỀN (đổi quy chế / bầu chủ nhóm / đổi cấu hình).
 *    ⇒ contract response **KHÔNG CÓ** `ledgerTxId` (đối lập `castVoteResponseSchema` của expense-vote):
 *    không có bút toán nào để trả về, và việc thiếu field này là CHỐT chống lập lờ "governance có
 *    đụng tiền không". Máy vote (weighted/veto/quorum/silent/tie-break) DÙNG LẠI y nguyên của F2/F3.
 *
 * POST /v1/groups/:id/governance/decisions   — mở phiên (owner/admin/treasurer)
 * POST /v1/governance/decisions/:id/votes    — bỏ/đổi phiếu (mọi member ACTIVE)
 * GET  /v1/governance/decisions/:id          — trạng thái phiên + phiếu của tôi
 * ========================================================================= */

/** Loại chủ thể của 1 phiên `consensus` — cột `Consensus.subjectType`. */
export const CONSENSUS_SUBJECT_TYPE = {
  EXPENSE: "expense",
  GOVERNANCE: "governance",
} as const;

export type ConsensusSubjectType =
  (typeof CONSENSUS_SUBJECT_TYPE)[keyof typeof CONSENSUS_SUBJECT_TYPE];

export const consensusSubjectTypeSchema = z.enum([
  CONSENSUS_SUBJECT_TYPE.EXPENSE,
  CONSENSUS_SUBJECT_TYPE.GOVERNANCE,
]);

/** Loại quyết định governance — cột `Consensus.governanceKind` (CHECK DB cùng whitelist). */
export const GOVERNANCE_KIND = {
  /** Đổi quy chế nhóm (ghi vào `group.settings`, KHÔNG chạm engine tiền). */
  RULE_CHANGE: "rule_change",
  /** Bầu / chuyển chủ nhóm (passed ⇒ reuse OwnershipTransferService). */
  OWNER_TRANSFER: "owner_transfer",
  /** Đổi cấu hình nhóm (ghi vào `group.settings` — override system_settings ở tầng group). */
  CONFIG_CHANGE: "config_change",
  /**
   * 🔴🔴 US-AI-0718-S24 (@UC9958 · sổ 08/09 mục B-32 §7 · QĐ người dùng 08/09/2026) — ĐỔI THỦ QUỸ.
   *
   * VÌ SAO CÓ: đo 08/09 thấy *"mở biểu quyết đổi thủ quỹ"* không có loại nào nhận, nên nó rơi sang
   * `owner_transfer` (SAI NGƯỜI) hoặc buộc người dùng chọn đại một loại gần đúng.
   *
   * ⚠️ `one_treasurer_per_group` (partial unique index) ⇒ đây là phép **HOÁN ĐỔI NGUYÊN TỬ**, không
   * phải phép gán: thủ quỹ cũ phải bị hạ về `member` trong CÙNG transaction, nếu không index ném.
   * Thân swap ĐÃ CÓ ở `RoleManagementService` (kèm chốt chống "thủ quỹ ma") — passed ⇒ tái dùng
   * nguyên thân đó, KHÔNG viết cơ chế thứ hai (`CLAUDE.md` §3.8).
   *
   * 🔴 **0 BÚT TOÁN.** Đổi người giữ quỹ KHÔNG di chuyển một đồng nào; nó đổi *ai được bấm*, không
   * đổi *tiền đang ở đâu*. Ai thêm ledger vào đường này là đang bịa một giao dịch không có thật.
   */
  TREASURER_CHANGE: "treasurer_change",
} as const;

export type GovernanceKind = (typeof GOVERNANCE_KIND)[keyof typeof GOVERNANCE_KIND];

export const governanceKindSchema = z.enum([
  GOVERNANCE_KIND.RULE_CHANGE,
  GOVERNANCE_KIND.OWNER_TRANSFER,
  GOVERNANCE_KIND.CONFIG_CHANGE,
  GOVERNANCE_KIND.TREASURER_CHANGE,
]);

/** Khoá config governance — TÁCH KHỎI `expense_decision_*` (quyết định phi-tiền có luật riêng). */
export const GOVERNANCE_DECISION_MODE_KEY = "governance_decision_mode";
export const GOVERNANCE_DECISION_THRESHOLD_KEY = "governance_decision_threshold";
export const GOVERNANCE_QUORUM_MIN_KEY = "governance_quorum_min";
export const GOVERNANCE_DEADLINE_HOURS_KEY = "governance_deadline_hours";

/** Cơ chế governance: majority (mặc định) | ratio. `silent`/`admin_only` KHÔNG áp cho governance. */
export const GOVERNANCE_MODES = ["majority", "ratio"] as const;
export type GovernanceMode = (typeof GOVERNANCE_MODES)[number];
export const governanceModeSchema = z.enum(GOVERNANCE_MODES);

export const DEFAULT_GOVERNANCE_MODE: GovernanceMode = "majority";
export const DEFAULT_GOVERNANCE_THRESHOLD = 0.6;
export const DEFAULT_GOVERNANCE_QUORUM_MIN = 0;
export const DEFAULT_GOVERNANCE_DEADLINE_HOURS = 168;

/** Độ dài tối đa key/value config (chống payload phình + injection nhãn). */
export const GOVERNANCE_KEY_MAX = 64;
export const GOVERNANCE_VALUE_MAX = 256;
export const GOVERNANCE_TITLE_MAX = 200;

/**
 * Payload theo loại (discriminated union — validate tại BIÊN, KHÔNG nhận field lạ):
 *  - rule_change / config_change ⇒ `{ key, value }` (value scalar: string | number | boolean).
 *  - owner_transfer              ⇒ `{ newOwnerMemberId }` (GroupMember.id).
 * KHÔNG chứa tiền (B1 không áp — governance phi-tiền) và KHÔNG PII.
 */
const governanceConfigValueSchema = z.union([
  z.string().max(GOVERNANCE_VALUE_MAX),
  z.number(),
  z.boolean(),
]);

export const governanceConfigPayloadSchema = z
  .object({
    key: z.string().min(1).max(GOVERNANCE_KEY_MAX),
    value: governanceConfigValueSchema,
  })
  .strict();

export const governanceOwnerTransferPayloadSchema = z
  .object({
    newOwnerMemberId: z.string().uuid(),
  })
  .strict();

/**
 * 🔴 US-AI-0718-S24 (@UC9958) — payload ĐỔI THỦ QUỸ: đúng MỘT ghế thành viên.
 * Cùng hình dạng `governanceOwnerTransferPayloadSchema` nhưng **schema RIÊNG**, không dùng chung:
 * gộp lại thì một payload `newOwnerMemberId` lọt được vào phiên đổi thủ quỹ và ngược lại — đúng loại
 * trạng-thái-sai mà `.strict()` sinh ra để làm cho **không biểu diễn được**.
 */
export const governanceTreasurerChangePayloadSchema = z
  .object({
    newTreasurerMemberId: z.string().uuid(),
  })
  .strict();

export type GovernanceTreasurerChangePayload = z.infer<
  typeof governanceTreasurerChangePayloadSchema
>;

export type GovernanceConfigPayload = z.infer<typeof governanceConfigPayloadSchema>;
export type GovernanceOwnerTransferPayload = z.infer<
  typeof governanceOwnerTransferPayloadSchema
>;

/** Body tạo phiên. Union theo `governanceKind` (superRefine — payload PHẢI khớp loại). */
export const createGovernanceDecisionRequestSchema = z
  .object({
    governanceKind: governanceKindSchema,
    title: z.string().min(1).max(GOVERNANCE_TITLE_MAX),
    payload: z.unknown(),
  })
  .strict()
  .superRefine((data, ctx) => {
    // 🔴 US-AI-0718-S24 (@UC9958) — mỗi loại MỘT schema payload; không có nhánh "dùng tạm cái gần giống".
    const schema =
      data.governanceKind === GOVERNANCE_KIND.OWNER_TRANSFER
        ? governanceOwnerTransferPayloadSchema
        : data.governanceKind === GOVERNANCE_KIND.TREASURER_CHANGE
          ? governanceTreasurerChangePayloadSchema
          : governanceConfigPayloadSchema;
    const parsed = schema.safeParse(data.payload);
    if (!parsed.success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["payload"],
        message: "payload không hợp lệ cho loại quyết định này.",
      });
    }
  });

export type CreateGovernanceDecisionRequest = z.infer<
  typeof createGovernanceDecisionRequestSchema
>;

/** Trạng thái phiên governance — CÙNG bộ với consensus (open|passed|failed|expired). */
export const governanceStatusSchema = z.enum(["open", "passed", "failed", "expired"]);
export type GovernanceStatus = z.infer<typeof governanceStatusSchema>;

/** Bộ đếm phiếu (mirror `voteTallySchema` — đếm đầu người + mẫu số SNAPSHOT). */
export const governanceTallySchema = z
  .object({
    yes: z.number().int().nonnegative(),
    no: z.number().int().nonnegative(),
    abstain: z.number().int().nonnegative(),
    /** Mẫu số = eligibleCount SNAPSHOT lúc mở phiên (chống lách người — P3.4). */
    totalEligible: z.number().int().nonnegative(),
  })
  .strict();

export type GovernanceTally = z.infer<typeof governanceTallySchema>;

/**
 * Response tạo phiên / đọc phiên. 🔴 KHÔNG có `ledgerTxId` — governance KHÔNG post ledger.
 * `appliedAt` = mốc ÁP hành động (config ghi / owner đổi); null khi chưa passed hoặc chưa áp.
 */
export const governanceDecisionResponseSchema = z
  .object({
    decisionId: z.string().uuid(),
    groupId: z.string().uuid(),
    subjectType: z.literal(CONSENSUS_SUBJECT_TYPE.GOVERNANCE),
    governanceKind: governanceKindSchema,
    title: z.string(),
    mechanism: governanceModeSchema,
    thresholdRatio: z.number().nullable(),
    status: governanceStatusSchema,
    deadline: z.string().datetime().nullable(),
    tally: governanceTallySchema,
    myVote: z.enum(["yes", "no", "abstain"]).nullable(),
    appliedAt: z.string().datetime().nullable(),
    openedAt: z.string().datetime(),
  })
  .strict();

export type GovernanceDecisionResponse = z.infer<typeof governanceDecisionResponseSchema>;

/**
 * Response ĐỌC phiên (`GET /governance/decisions/:id`) = response phiên + lá phiếu của người xem (US-AIV2-0112 CONSENSUS-MOBILE ·
 * @UC0357). Tách khỏi `governanceDecisionResponseSchema` (dùng chung với lượt MỞ phiên) để `ballot` BẮT BUỘC ở cửa đọc — không có hình
 * "GET thiếu lá phiếu".
 */
export const governanceDecisionDetailResponseSchema = governanceDecisionResponseSchema
  .extend({ ballot: consensusBallotViewSchema })
  .strict();

export type GovernanceDecisionDetailResponse = z.infer<typeof governanceDecisionDetailResponseSchema>;

/** Body bỏ phiếu governance (mirror expense `castVoteRequestSchema`). */
export const castGovernanceVoteRequestSchema = z
  .object({
    choice: z.enum(["yes", "no", "abstain"]),
  })
  .strict();

export type CastGovernanceVoteRequest = z.infer<typeof castGovernanceVoteRequestSchema>;

/**
 * Response bỏ phiếu. 🔴 KHÔNG có `ledgerTxId` (đối lập expense-vote) — governance KHÔNG BAO GIỜ
 * post ledger. `.strict()` ⇒ nếu ai đó "vô tình" nhét ledgerTxId vào, parse FAIL ngay tại biên.
 */
export const castGovernanceVoteResponseSchema = z
  .object({
    decisionId: z.string().uuid(),
    governanceKind: governanceKindSchema,
    myVote: z.enum(["yes", "no", "abstain"]),
    tally: governanceTallySchema,
    decisionStatus: governanceStatusSchema,
    /** Hành động đã ÁP sau passed (true) / chưa (false). failed ⇒ luôn false. */
    applied: z.boolean(),
    /** US-AIV2-0112 CONSENSUS-FIRST-VOTE (@UC0356): suất đã có phiếu của người khác — lần bấm này KHÔNG ghi cho chúng. */
    seatsVotedByOthers: z.array(seatVotedByOtherSchema),
  })
  .strict();

export type CastGovernanceVoteResponse = z.infer<typeof castGovernanceVoteResponseSchema>;

/** Thông điệp lỗi (VN, không lộ nội bộ). */
export const GOVERNANCE_CLOSED_MESSAGE = "Phiên biểu quyết đã đóng — không thể bỏ phiếu.";
export const GOVERNANCE_NOT_FOUND_MESSAGE = "Phiên biểu quyết không tồn tại.";
